import React, {
  useState,
  useRef,
  useImperativeHandle,
  useEffect,
  forwardRef,
  useMemo,
} from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Alignment,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Highlight,
  HorizontalLine,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  RemoveFormat,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
} from "ckeditor5";

import './Settings.css'
import "ckeditor5/ckeditor5.css";
import { useDispatch } from "react-redux";
import { currentSelectedLanguage, updateSettingsData } from "../../../StateManagement/actions";
import { useSelector } from "react-redux";

const LICENSE_KEY = "GPL";


const TermsOfService = forwardRef(
  (
    { content = "", onUserEdit, isReadOnly = false, isDisable = false },
    ref
  ) => {
    const selectedLanguage=useSelector(currentSelectedLanguage);
    const dispatch = useDispatch();
    const [editorData, setEditorData] = useState(selectedLanguage.LstLanguageCodes.UI_ByContinuingTheProcess);
    const editorRef = useRef(null);
    const skipChange = useRef(false);
    const isInitialMount = useRef(true);
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const outerTabSelected = useSelector(state => state.settings.settingFor);
    const debounceTimer = useRef(null);


    // Sync editor content with external changes
    useEffect(() => {
      if (content && content !== editorData) {
        setEditorData(content);
        if (editorRef.current) {
          skipChange.current = true;
          editorRef.current.setData(content);
        }
      }
      else if(content ==='' && isDisable) {
        setEditorData(selectedLanguage.LstLanguageCodes.UI_ByContinuingTheProcess);

      }
    }, [content]);

    useEffect(() => {
      setIsLayoutReady(true);
      return () => setIsLayoutReady(false);
    }, []);

    useEffect(() => {
      return () => {
        if (editorRef.current) {
          editorRef.current.destroy();
        }
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }, []);

    useImperativeHandle(ref, () => ({
      getContent: () => editorData,
      focus: () => {
        if (editorRef.current) {
          editorRef.current.editing.view.focus();
        }
      },
    }));

    const handleChange = (event, editor) => {
      const data = editor.getData();
      setEditorData(data);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (isInitialMount.current) {
        return;
      }
      debounceTimer.current = setTimeout(() => {
        if (!skipChange.current) {
          onUserEdit?.(data);
          dispatch(
            updateSettingsData({
              type: "TermsOfService",
              value: data,
              outerTabSelected: outerTabSelected === "User" ? 'Personal' : outerTabSelected,
              level: 'common',
            })
          );
        }
        skipChange.current = false;
      }, 500); 
    };

    const { editorConfig } = useMemo(() => {
      if (!isLayoutReady) return {};

      return {
        editorConfig: {
          toolbar: {
            items: [
              "heading",
              "bold",
              "italic",
              "underline",
              "numberedList",
              "bulletedList",
              "link",
              "alignment",
              "undo",
              "redo",
            ],
            shouldNotGroupWhenFull: true,
          },
          plugins: [
            Alignment,
            Autosave,
            BlockQuote,
            Bold,
            Code,
            CodeBlock,
            Essentials,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            Heading,
            Highlight,
            HorizontalLine,
            Indent,
            IndentBlock,
            Italic,
            Link,
            List,
            ListProperties,
            Paragraph,
            RemoveFormat,
            Strikethrough,
            Subscript,
            Superscript,
            Underline,
          ],
          fontFamily: { supportAllValues: true },
          fontSize: {
            options: [10, 12, 14, "default", 18, 20, 22],
            supportAllValues: true,
          },
          heading: {
            options: [
              {
                model: "paragraph",
                title: "Paragraph",
                class: "ck-heading_paragraph",
              },
              {
                model: "heading1",
                view: "h1",
                title: "Heading 1",
                class: "ck-heading_heading1",
              },
              {
                model: "heading2",
                view: "h2",
                title: "Heading 2",
                class: "ck-heading_heading2",
              },
              {
                model: "heading3",
                view: "h3",
                title: "Heading 3",
                class: "ck-heading_heading3",
              }
              
            ],
          },
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: "https://"
          },
          licenseKey: LICENSE_KEY,
        },
      };
    }, [isLayoutReady]);

    return (
      <div
        className="main-container"
        style={{
          opacity: isDisable ? 0.5 : 1,
          pointerEvents: isDisable ? "none" : "auto",
        }}>
        <div className="editor-container editor-container_classic-editor">
          <div className="editor-container__editor">
            {editorConfig && (
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={editorData}
                disabled={isReadOnly}
                onReady={(editor) => {
                  editorRef.current = editor;
                  isInitialMount.current = false;
                }}
                onChange={handleChange}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default TermsOfService;
